import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

export type BadgeVariant = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default';
export type BadgeSize = 'small' | 'medium' | 'large';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  sx?: SxProps<Theme>;
}

const variantStyles: Record<BadgeVariant, SxProps<Theme>> = {
  primary: {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
  },
  secondary: {
    backgroundColor: 'secondary.main',
    color: 'secondary.contrastText',
  },
  success: {
    backgroundColor: 'success.main',
    color: 'success.contrastText',
  },
  error: {
    backgroundColor: 'error.main',
    color: 'error.contrastText',
  },
  warning: {
    backgroundColor: 'warning.main',
    color: 'warning.contrastText',
  },
  info: {
    backgroundColor: 'info.main',
    color: 'info.contrastText',
  },
  default: {
    backgroundColor: 'grey.200',
    color: 'text.primary',
  },
};

const sizeStyles: Record<BadgeSize, SxProps<Theme>> = {
  small: {
    fontSize: '0.6875rem',
    px: 1,
    py: 0.25,
    height: '18px',
  },
  medium: {
    fontSize: '0.75rem',
    px: 1.5,
    py: 0.5,
    height: '24px',
  },
  large: {
    fontSize: '0.875rem',
    px: 2,
    py: 0.75,
    height: '32px',
  },
};

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'small',
  sx,
}) => {
  return (
    <Box
      component="span"
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1,
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        },
        variantStyles[variant],
        sizeStyles[size],
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
};
